import { lockScroll } from "@scripts/scrollLock";

import type { PageHold } from "./SiteHeader.inert";
import { freePage, isolate } from "./SiteHeader.inert";
import type { EPanel } from "./SiteHeader.store";
import { followPanel, getHeaderStore, releasePanel } from "./SiteHeader.store";

type PanelSpec = {
	key: EPanel;
	root: HTMLElement;
	/** Registered with the store: whose transition ends when this panel has finished leaving. */
	settles: HTMLElement;
	returnTo: () => HTMLElement | undefined;
	isOpen: () => boolean;
	/** The panel's own visual teardown, run before the page hold is given back. */
	release: () => void;
	/** Subtrees the page keeps while this panel holds it. Defaults to the header alone. */
	keep?: () => readonly Element[];
};

type Panel = {
	hold: () => void;
	close: (returnFocus: boolean) => void;
	dispose: () => void;
};

/** The lifecycle every header panel shares. It deliberately owns neither how the field is taken —
    the megamenu seizes it where its peers hand over — nor Escape, which unwinds a level first in
    the drawer. */
export const createPanel = ({
	key,
	root,
	settles,
	returnTo,
	isOpen,
	release,
	keep,
}: PanelSpec): Panel => {
	const store = getHeaderStore(root);
	const page: PageHold = {};

	store.register(key, settles);

	const dismiss = () => {
		release();
		freePage(page);
		store.holdPage(key, false);
	};

	const close = (returnFocus: boolean) => {
		if (!isOpen()) return;

		/* Read before dismissing: the megamenu's is whichever trigger is expanded, and its own
		   release clears that attribute — asked afterwards it answers `undefined`. */
		const back = returnTo();

		dismiss();
		releasePanel(store, key);

		if (returnFocus) back?.focus();
	};

	const unfollow = followPanel(store, key, isOpen, dismiss);

	return {
		/* Idempotent: the lock counts holds, so a hand-over keeps the page held across the swap. */
		hold: () => {
			page.release ??= lockScroll();
			page.unisolate ??= isolate(root, keep?.() ?? [root]);
			store.holdPage(key, true);
		},
		close,
		dispose: () => {
			unfollow();
			dismiss();
		},
	};
};
